// src/components/desk/AdRemoverPanel.jsx - AD REMOVER (owner 2026-10-23: "Now we need to build ad remover. To delete ads
// automatically when manager tells it from panel").
//
// Our agents' LIVE adverts of this flat (ss / myhome / korter), each with its removal state. A manager / admin ticks some
// (or all) and the server queues one removal job per advert on the PC of the agent who posted it - only that account can
// delete it (share_plans.js /api/core/ad-takedown, extension sntvDeleteAdvert). The server answers 403 to anyone else,
// and then nothing is drawn: a control that always fails is a lock with nothing behind it.
import { useEffect, useState } from 'react'
import { t } from '../../lib/i18n.js'
import { btn } from './primitives.jsx'
import { getAdTakedown, requestAdTakedown } from '../../db/core.js'
import { askRemovalReason } from './RemovalReason.jsx'

const PORTAL = { ss: 'SS', myhome: 'Myhome', korter: 'Korter' }

export function AdRemoverPanel({ propertyId }) {
  const [rows, setRows] = useState(null)
  const [sel, setSel] = useState(() => new Set())
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [refresh, setRefresh] = useState(0)

  useEffect(() => {
    if (!propertyId) return
    let gone = false
    const load = () => getAdTakedown(propertyId)
      .then(d => { if (!gone) { setRows(d.adverts || []); setErr(null) } })
      .catch(() => { if (!gone) setRows(null) })            // 403 for non-managers: nothing drawn
    load()
    const tm = setInterval(load, 20000)                     // removals finish on the agents' PCs - keep the states fresh
    return () => { gone = true; clearInterval(tm) }
  }, [propertyId, refresh])

  if (!rows) return null
  const live = rows.filter(r => r.status === 'active')
  const pending = r => r.removal && ['scheduled', 'queued'].includes(r.removal.state)

  async function remove(ids) {
    if (!ids.length) return
    const why = await askRemovalReason(ids.length)        // owner 2026-10-28: every removal says why
    if (!why) return
    setBusy(true)
    try { await requestAdTakedown(ids, why.reason, why.note); setSel(new Set()); setRefresh(x => x + 1) }
    catch (e) { setErr(e.message) }
    finally { setBusy(false) }
  }

  return (
    <section className="rounded-lg border border-border p-3">
      <h4 className="text-xs font-medium mb-1">{t('desk_adrm_title')} ({live.length})</h4>
      {!rows.length && <p className="text-xs text-muted-foreground">{t('desk_adrm_none')}</p>}
      <div className="space-y-1">
        {rows.map(r => {
          const st = r.removal?.state
          const label = r.status !== 'active' ? (st === 'published' ? t('desk_adrm_state_published') : r.status)
            : st ? t(`desk_adrm_state_${st}`) : ''
          return (
            <label key={r.listing_id} className="flex items-center gap-2 text-xs">
              <input type="checkbox" disabled={busy || r.status !== 'active' || pending(r)}
                checked={sel.has(r.listing_id)}
                onChange={e => setSel(s => { const n = new Set(s); e.target.checked ? n.add(r.listing_id) : n.delete(r.listing_id); return n })} />
              <span className="w-14 shrink-0">{PORTAL[r.platform] || r.platform}</span>
              <a href={r.url} target="_blank" rel="noreferrer" className="underline shrink-0">{r.advert_id}</a>
              <span className="text-muted-foreground truncate">{String(r.agent || '').split('@')[0]}</span>
              {label && <span className={`ml-auto shrink-0 ${st === 'error' ? 'text-red-600' : 'text-muted-foreground'}`}
                title={[r.removal?.reason && t(`desk_adrm_why_${r.removal.reason}`), r.removal?.note, r.removal?.message].filter(Boolean).join(' · ')}>{label}</span>}
            </label>
          )
        })}
      </div>
      {live.length > 0 && (
        <div className="flex gap-2 mt-2">
          <button type="button" className={btn('ghost')} disabled={busy || !sel.size} onClick={() => remove([...sel])}>
            {t('desk_adrm_remove_sel')}
          </button>
          <button type="button" className={btn('ghost')} disabled={busy}
            onClick={() => remove(live.filter(r => !pending(r)).map(r => r.listing_id))}>
            {t('desk_adrm_remove_all')}
          </button>
        </div>
      )}
      <p className="text-[11px] text-muted-foreground/80 mt-1">{t('desk_adrm_note')}</p>
      {err && <p className="text-[11px] text-red-600 mt-1">{err}</p>}
    </section>
  )
}
